import type { HeroImageCrop, RichText } from '@diana/contracts';
import { useLayoutEffect, useRef, useState } from 'react';
import serifFont from '@fontsource/cormorant-garamond/files/cormorant-garamond-latin-500-normal.woff2?url';
import sansFont from '@fontsource/space-grotesk/files/space-grotesk-latin-400-normal.woff2?url';
import sansLightFont from '@fontsource/space-grotesk/files/space-grotesk-latin-300-normal.woff2?url';
import heroCss from '../../../../site/src/styles/components/wedding-hero.css?raw';
import { richTextToHtml } from '../../components/CompactRichTextField.js';
import './WeddingHeroPreview.css';

type PreviewImage = { url: string; crop?: HeroImageCrop };
const sizes = {
  Desktop: { width: 1280, height: 800 },
  Mobile: { width: 390, height: 844 },
  'Small phone': { width: 375, height: 667 },
};
type Mode = keyof typeof sizes;
type Props = {
  heading: string;
  description: RichText;
  buttonLabel: string;
  bookingMessage: string;
  desktopImage?: PreviewImage;
  mobileImage?: PreviewImage;
  composed?: boolean;
};
export function WeddingHeroPreview(props: Props) {
  const [mode, setMode] = useState<Mode>('Mobile');
  const frame = useRef<HTMLDivElement>(null);
  const [available, setAvailable] = useState(390);
  useLayoutEffect(() => {
    const element = frame.current;
    if (!element) return;
    const update = () => setAvailable(element.getBoundingClientRect().width || 390);
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const size = sizes[mode];
  const scale = Math.min(1, available / size.width);
  const image = mode === 'Desktop' ? props.desktopImage : (props.mobileImage ?? props.desktopImage);
  return (
    <section className="wedding-hero-preview" aria-label="Hero preview">
      <div className="wedding-hero-preview__heading">
        <h3>Hero preview</h3>
        <span>
          {size.width} × {size.height}
        </span>
      </div>
      <div className="wedding-hero-preview__tabs" role="tablist" aria-label="Preview screen size">
        {(Object.keys(sizes) as Mode[]).map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={mode === name}
            onClick={() => setMode(name)}
          >
            {name}
          </button>
        ))}
      </div>
      <div
        ref={frame}
        className="wedding-hero-preview__frame"
        style={{ height: size.height * scale, maxWidth: size.width }}
      >
        <iframe
          title={`Hero preview: ${mode}`}
          sandbox="allow-same-origin"
          srcDoc={previewDocument(props, image, mode)}
          width={size.width}
          height={size.height}
          style={{ transform: `scale(${scale})` }}
        />
      </div>
      <p className="wedding-hero-preview__hint">
        Check the heading, photograph and booking button before scrolling. Text is shared across all
        screen sizes.
      </p>
    </section>
  );
}
function previewDocument(props: Props, image: PreviewImage | undefined, mode: Mode) {
  const mobile = mode !== 'Desktop';
  const crop = image?.crop;
  const cropStyle = crop
    ? `position:absolute;width:${100 / crop.width}%;max-width:none;height:${100 / crop.height}%;left:${(-crop.x / crop.width) * 100}%;top:${(-crop.y / crop.height) * 100}%;border-radius:0;`
    : '';
  const photo = image
    ? `<figure class="wed-hero-media"><div style="${crop ? `position:relative;overflow:hidden;aspect-ratio:${mobile ? '4/3' : '4/5'};border-radius:6px;` : ''}"><img class="wed-hero-image" src="${escape(image.url)}" alt="Hero photograph" style="${cropStyle}" /></div></figure>`
    : '<figure class="wed-hero-media"><div class="photo-empty">Choose a Hero photograph to preview the layout.</div></figure>';
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>
 @font-face{font-family:'Cormorant Garamond';font-weight:500;font-style:normal;src:url('${serifFont}') format('woff2')}
 @font-face{font-family:'Space Grotesk';font-weight:400;font-style:normal;src:url('${sansFont}') format('woff2')}
 @font-face{font-family:'Space Grotesk';font-weight:300;font-style:normal;src:url('${sansLightFont}') format('woff2')}
 :root{--bg:#fff;--ink:#0e0e0e;--muted:#747474;--line:#dedede;--serif:'Cormorant Garamond',Georgia,serif;--sans:'Space Grotesk',Arial,sans-serif;--maxw:1320px;--pad:clamp(20px,3.5vw,48px)}
 *{box-sizing:border-box;margin:0;padding:0}html{overflow-y:scroll}body{background:var(--bg);color:var(--ink);font-family:var(--sans);font-weight:300}.container{max-width:var(--maxw);margin:0 auto;padding-inline:var(--pad)}button{font-family:inherit}.preview-nav{position:absolute;top:0;left:0;right:0;height:64px;display:flex;align-items:center;justify-content:space-between;padding:0 var(--pad);border-bottom:1px solid #eee}.preview-nav strong{font:500 23px var(--serif)}.preview-nav span{font-size:10px;letter-spacing:.15em}.photo-empty{background:#f2f4f7;min-height:220px;padding:30px;display:grid;place-items:center;color:#69727f;font-size:14px;text-align:center}
 ${heroCss}
 </style></head><body><nav class="preview-nav"><strong>Diana Perova♡</strong><span>${mobile ? 'MENU' : 'PORTFOLIO　 WEDDINGS　 PRICING　 ABOUT'}</span></nav><main><header class="wed-hero container${props.composed ? ' wed-hero--composed' : ''}"><div class="wed-hero-copy"><h1>${escape(props.heading)}</h1><div class="wed-lead">${richTextToHtml(props.description)}</div><div class="wed-booking-panel"><span class="wed-booking-corner wed-booking-corner--tl"></span><span class="wed-booking-corner wed-booking-corner--tr"></span><span class="wed-booking-corner wed-booking-corner--bl"></span><span class="wed-booking-corner wed-booking-corner--br"></span><p class="wed-booking">${escape(props.bookingMessage)}</p><button class="wed-hero-action" type="button">${escape(props.buttonLabel)}</button></div></div>${photo}</header></main></body></html>`;
}
function escape(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
